import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * Al-Qur'an picker with TWO modes:
 *  - "single" (default): pick Juz → Surah → range ayat within one surah.
 *  - "range": pick Surah Awal → Surah Akhir (lintas surah, e.g. An-Naba' → An-Nas).
 *
 * onChange(payload) fires with:
 *   single: { surah_no, surah_nama, juz, ayat_dari, ayat_sampai, halaman }
 *   range : { mode:"range", surah_dari, surah_dari_nama, surah_sampai, surah_sampai_nama, juz? }
 */
export default function QuranPicker({ value = {}, onChange }) {
  const [mode, setMode] = useState(value.mode === "range" ? "range" : "single");
  const [surahs, setSurahs] = useState([]);
  const [juzList, setJuzList] = useState([]);
  const [state, setState] = useState({
    juz: value.juz || "",
    surah_no: value.surah_no || "",
    ayat_dari: value.ayat_dari || "",
    ayat_sampai: value.ayat_sampai || "",
    surah_dari: value.surah_dari || "",
    surah_sampai: value.surah_sampai || "",
  });
  const [surahsInJuz, setSurahsInJuz] = useState([]);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    api.get("/quran/surahs").then((r) => setSurahs(r.data));
    api.get("/quran/juz").then((r) => setJuzList(r.data));
  }, []);

  useEffect(() => {
    if (!state.juz) { setSurahsInJuz([]); return; }
    api.get(`/quran/juz/${state.juz}/surahs`).then((r) => setSurahsInJuz(r.data));
  }, [state.juz]);

  const currentSurah = useMemo(() => surahs.find((s) => s.nomor === Number(state.surah_no)), [surahs, state.surah_no]);
  const surahInThisJuz = useMemo(() => surahsInJuz.find((s) => s.surah === Number(state.surah_no)), [surahsInJuz, state.surah_no]);

  // Single mode: validate + emit onChange with ayat range
  useEffect(() => {
    if (mode !== "single") return;
    (async () => {
      if (!state.surah_no || !state.ayat_dari || !state.ayat_sampai) {
        setValidation(null); return;
      }
      try {
        const { data } = await api.post("/quran/validate", {
          surah: Number(state.surah_no),
          ayat_dari: Number(state.ayat_dari),
          ayat_sampai: Number(state.ayat_sampai),
          juz: state.juz ? Number(state.juz) : null,
        });
        setValidation({ ok: true, ...data });
        onChange?.({
          surah_no: Number(state.surah_no),
          surah_nama: data.surah_nama,
          juz: data.juz_start,
          ayat_dari: Number(state.ayat_dari),
          ayat_sampai: Number(state.ayat_sampai),
          halaman: data.halaman_start,
        });
      } catch (e) {
        setValidation({ ok: false, msg: e.response?.data?.detail || e.message });
      }
    })();
    // eslint-disable-next-line
  }, [mode, state.surah_no, state.ayat_dari, state.ayat_sampai, state.juz]);

  // Range mode: emit onChange when both surah_dari & surah_sampai selected
  useEffect(() => {
    if (mode !== "range") return;
    const dari = Number(state.surah_dari), sampai = Number(state.surah_sampai);
    if (!dari || !sampai) { setValidation(null); return; }
    if (sampai < dari) { setValidation({ ok: false, msg: "Surah Akhir harus setelah Surah Awal" }); return; }
    const sd = surahs.find((s) => s.nomor === dari);
    const ss = surahs.find((s) => s.nomor === sampai);
    setValidation({ ok: true, range: true, surah_dari_nama: sd?.nama, surah_sampai_nama: ss?.nama, count: sampai - dari + 1 });
    onChange?.({
      mode: "range",
      surah_dari: dari, surah_dari_nama: sd?.nama,
      surah_sampai: sampai, surah_sampai_nama: ss?.nama,
      juz: state.juz ? Number(state.juz) : undefined,
    });
    // eslint-disable-next-line
  }, [mode, state.surah_dari, state.surah_sampai, state.juz, surahs]);

  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap" data-testid="quran-mode-toggle">
        <button type="button" onClick={() => setMode("single")} data-testid="quran-mode-single"
          className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "single" ? "bg-[#14532d] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Satu Surah (Ayat Range)
        </button>
        <button type="button" onClick={() => setMode("range")} data-testid="quran-mode-range"
          className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "range" ? "bg-[#14532d] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Surah Range (Lintas Surah)
        </button>
      </div>

      {mode === "single" && (
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs">Juz</label>
            <select className="imsyaf-input" data-testid="quran-juz" value={state.juz}
              onChange={(e) => setState({ ...state, juz: e.target.value, surah_no: "", ayat_dari: "", ayat_sampai: "" })}>
              <option value="">Pilih Juz…</option>
              {juzList.map((j) => (<option key={j.juz} value={j.juz}>Juz {j.juz} — {j.surah_nama}:{j.ayat}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs">Surah</label>
            <select className="imsyaf-input" data-testid="quran-surah" value={state.surah_no}
              onChange={(e) => {
                const n = e.target.value;
                const s = surahsInJuz.find((x) => x.surah === Number(n));
                setState({ ...state, surah_no: n, ayat_dari: s ? s.ayat_dari : "", ayat_sampai: s ? s.ayat_dari : "" });
              }} disabled={!state.juz}>
              <option value="">Pilih Surah…</option>
              {surahsInJuz.map((s) => (<option key={s.surah} value={s.surah}>{s.surah}. {s.nama} — ayat {s.ayat_dari}–{s.ayat_sampai}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs">Ayat Awal {currentSurah && <span className="text-slate-400">(1-{currentSurah.ayat})</span>}</label>
            <input type="number" className="imsyaf-input" data-testid="quran-ayat-dari"
              min={surahInThisJuz?.ayat_dari || 1} max={surahInThisJuz?.ayat_sampai || (currentSurah?.ayat || 300)}
              value={state.ayat_dari} onChange={(e) => setState({ ...state, ayat_dari: e.target.value })} disabled={!state.surah_no} />
          </div>
          <div>
            <label className="text-xs">Ayat Akhir {surahInThisJuz && <span className="text-slate-400">(≤ {surahInThisJuz.ayat_sampai})</span>}</label>
            <input type="number" className="imsyaf-input" data-testid="quran-ayat-sampai"
              min={state.ayat_dari || 1} max={surahInThisJuz?.ayat_sampai || (currentSurah?.ayat || 300)}
              value={state.ayat_sampai} onChange={(e) => setState({ ...state, ayat_sampai: e.target.value })} disabled={!state.surah_no} />
          </div>
        </div>
      )}

      {mode === "range" && (
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs">Juz (opsional — untuk memfilter daftar surah)</label>
            <select className="imsyaf-input" data-testid="quran-range-juz" value={state.juz}
              onChange={(e) => setState({ ...state, juz: e.target.value })}>
              <option value="">Semua Juz</option>
              {juzList.map((j) => (<option key={j.juz} value={j.juz}>Juz {j.juz}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs">Surah Awal *</label>
            <select className="imsyaf-input" data-testid="quran-surah-dari" value={state.surah_dari}
              onChange={(e) => setState({ ...state, surah_dari: e.target.value })}>
              <option value="">Pilih Surah Awal…</option>
              {(state.juz ? surahsInJuz.map((x) => ({ nomor: x.surah, nama: x.nama })) : surahs).map((s) => (
                <option key={s.nomor} value={s.nomor}>{s.nomor}. {s.nama}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs">Surah Akhir *</label>
            <select className="imsyaf-input" data-testid="quran-surah-sampai" value={state.surah_sampai}
              onChange={(e) => setState({ ...state, surah_sampai: e.target.value })}>
              <option value="">Pilih Surah Akhir…</option>
              {(state.juz ? surahsInJuz.map((x) => ({ nomor: x.surah, nama: x.nama })) : surahs)
                .filter((s) => !state.surah_dari || s.nomor >= Number(state.surah_dari))
                .map((s) => (<option key={s.nomor} value={s.nomor}>{s.nomor}. {s.nama}</option>))}
            </select>
          </div>
        </div>
      )}

      {validation && (
        <div className="text-xs">
          {validation.ok ? (
            <div className="text-green-700">
              {validation.range ? (
                <>✓ Setoran lintas surah: <b>{validation.surah_dari_nama}</b> → <b>{validation.surah_sampai_nama}</b> ({validation.count} surah)</>
              ) : (
                <>✓ {validation.surah_nama} {validation.surah_arab} · Juz {validation.juz_start}{validation.cross_juz ? `–${validation.juz_end}` : ""} · Halaman Madinah mulai {validation.halaman_start}</>
              )}
            </div>
          ) : (
            <div className="text-red-600">✗ {validation.msg}</div>
          )}
        </div>
      )}
    </div>
  );
}
